import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const LANGS = [
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "en", label: "English", flag: "EN" },
  { code: "tr", label: "Türkçe", flag: "TR" },
];

export default function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const loc = useLocation();
  const nav = useNavigate();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  const choose = (code) => {
    setLang(code);
    try { localStorage.setItem("qrmp_lang", code); } catch (e) {}
    // Rewrite URL to switch language prefix while staying on the same logical page.
    // Public localized routes get a /{code}/... prefix (or "/" if code === "de" on home root).
    const path = loc.pathname || "/";
    const parts = path.split("/").filter(Boolean);
    const hasPrefix = parts.length > 0 && ["de", "en", "tr"].includes(parts[0]);
    const inner = hasPrefix ? "/" + parts.slice(1).join("/") : path;
    const innerNormalized = inner === "" ? "/" : inner;

    // Only rewrite if we're on a localized public page (these are the ones that
    // exist at both "/" and "/{lang}/..." variants).
    const LOCALIZED_PATHS = ["/", "/pricing", "/impressum", "/datenschutz"];
    if (!LOCALIZED_PATHS.includes(innerNormalized)) {
      return;
    }

    // "de" → "/", others → "/{code}/..."
    const newUrl = code === "de"
      ? `${innerNormalized}${loc.search}${loc.hash}`
      : `/${code}${innerNormalized === "/" ? "" : innerNormalized}${loc.search}${loc.hash}`;
    nav(newUrl, { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 rounded-full" data-testid="lang-switcher">
          <Globe className="h-4 w-4" />
          <span className="font-medium">{current.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => choose(l.code)}
            className={lang === l.code ? "bg-accent/10 font-semibold" : ""}
            data-testid={`lang-${l.code}`}
          >
            <span className="mr-2 font-mono text-xs">{l.flag}</span>
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
